<script setup lang="ts">
import { ref, watch } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { marked } from 'marked'
import TurndownService from 'turndown'

/**
 * Éditeur WYSIWYG (Quill) dont le v-model est du **Markdown**.
 * - chargement : markdown -> HTML (marked) injecté dans Quill
 * - sauvegarde : HTML Quill -> markdown (turndown) émis vers le parent
 *
 * Le contenu est stocké en markdown pour l'affichage côté portail client
 * (renderSimpleMarkdown). On reste donc compatible avec le rendu allégé du front.
 */

defineProps({
  name: { type: String, required: true },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  errorMessage: String,
})

// v-model : markdown
const modelValue = defineModel<string | null | undefined>()

const turndown = new TurndownService({
  headingStyle: 'atx', // titres en `#`/`##`/`###`
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
})

function mdToHtml(md: string): string {
  return marked.parse(md ?? '', { async: false }) as string
}

function htmlToMd(html: string): string {
  return turndown.turndown(html ?? '').trim()
}

// HTML interne piloté par Quill
const html = ref<string>(mdToHtml(modelValue.value ?? ''))

// Sync parent -> éditeur : ne réécrit que si le markdown courant diffère
// (évite la boucle quand c'est l'éditeur lui-même qui a déclenché la MAJ).
watch(modelValue, (md) => {
  const current = htmlToMd(html.value)
  if ((md ?? '') !== current) {
    html.value = mdToHtml(md ?? '')
  }
})

// Sync éditeur -> parent
function onHtmlUpdate(value: string) {
  html.value = value
  const md = htmlToMd(value)
  if (md !== (modelValue.value ?? '')) {
    modelValue.value = md
  }
}
</script>

<template>
  <div class="form-control w-full">
    <label v-if="label" class="label">
      <span class="label-text font-semibold">
        {{ label }} <span v-if="required" class="text-red-500">*</span>
      </span>
    </label>
    <div :class="{ 'opacity-50 pointer-events-none': disabled, 'border border-red-500 rounded': errorMessage }">
      <QuillEditor
        :name="name"
        :content="html"
        content-type="html"
        :placeholder="placeholder"
        theme="snow"
        class="bg-base-100"
        @update:content="onHtmlUpdate"
      />
    </div>
    <span v-if="errorMessage" class="text-red-500 text-sm mt-1 ml-1">{{ errorMessage }}</span>
  </div>
</template>

<style>
.ql-editor {
  min-height: 220px;
}
</style>
